import type { StreamStatusResponse } from '@injectivelabs/sdk-ts'
import type { StreamType } from '@/types'
import type { mitoStream } from '@/app/client/streams/mito'
import type { spotMarketStream } from '@/app/client/streams/spot'
import type { portfolioStream } from '@/app/client/streams/portfolio'
import type {
  oracleStream,
  derivativesMarketStream
} from '@/app/client/streams/derivative'

type Stream = ReturnType<StreamFn>

type StreamFn =
  | typeof mitoStream.streamVault
  | typeof mitoStream.streamTransfers
  | typeof spotMarketStream.streamTrades
  | typeof spotMarketStream.streamOrders
  | typeof oracleStream.streamOraclePrices
  | typeof mitoStream.streamHistoricalStaking
  | typeof derivativesMarketStream.streamTrades
  | typeof derivativesMarketStream.streamMarkets
  | typeof portfolioStream.streamAccountPortfolio
  | typeof derivativesMarketStream.streamPositions
  | typeof spotMarketStream.streamOrderbookUpdates
  | typeof mitoStream.streamStakingRewardsByAccount
  | typeof oracleStream.streamOraclePricesByMarkets
  | typeof mitoStream.streamVaultHolderSubscriptions

const MAX_RECONNECTION = 10

/**
 * Every stream we extend with
 * an end and a status callback
 * that we can use how we deem fit.
 *
 * When there is an end callback,
 * we are reconnecting the stream
 * */
export class StreamProvider {
  private streamManager: Map<
    StreamType,
    { args: any; fn: Function; stream: Stream }
  >

  private reconnectCount: Record<string, number> = {}

  constructor() {
    this.streamManager = new Map()
  }

  subscribe({ fn, key, args }: { args: any; fn: StreamFn; key: StreamType }) {
    if (this.streamManager.has(key)) {
      return
    }

    const argsWithCallbacks = {
      ...args,
      onEndCallback: (status?: StreamStatusResponse): any => {
        if (this.getReconnectCount(key) <= MAX_RECONNECTION) {
          setTimeout(() => {
            this.reconnect(key)
            this.incrementReconnectCount(key)
          }, 1000)
        } else {
          console.error(status)
        }
      },
      onStatusCallback: (_status: StreamStatusResponse): any => {
        //
      }
    }
    const stream = fn(argsWithCallbacks)

    this.streamManager.set(key, {
      stream,
      fn,
      args: argsWithCallbacks
    })
  }

  cancelAll(streamsToPersist: string[]) {
    this.streamManager.forEach((stream) => {
      const shouldKeepStream = streamsToPersist.some((name) =>
        stream.fn.name.endsWith(name)
      )

      if (shouldKeepStream) {
        return
      }

      stream.stream.unsubscribe()
    })
    this.streamManager = new Map()
  }

  cancel(key: StreamType) {
    if (!this.exists(key)) {
      return
    }

    this.streamManager.get(key)!.stream.unsubscribe()
    this.streamManager.delete(key)
  }

  private reconnect(key: StreamType) {
    if (!this.exists(key)) {
      return
    }

    const { fn, args } = this.streamManager.get(key)!
    const newStream = fn(args)

    this.streamManager.set(key, {
      stream: newStream,
      fn,
      args
    })
  }

  private incrementReconnectCount(key: string) {
    return (this.reconnectCount[key] = (this.reconnectCount[key] || 0) + 1)
  }

  private getReconnectCount(key: string) {
    return this.reconnectCount[key] || 0
  }

  private exists(key: StreamType) {
    return this.streamManager.has(key)
  }
}

export const streamProvider = new StreamProvider()
